import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CookieService {

  /**
   * Set cookie dengan domain support untuk subdomain - SECURED VERSION
   */
  setCookie(name: string, value: string, days = 7, domain?: string): void {
    const expires = new Date();
    expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
    
    let cookieString = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/`;
    
    // SECURITY FIX: Validasi domain yang lebih ketat
    if (domain) {
      // Validasi domain yang diberikan
      if (!this.isValidDomain(domain)) {
        console.error('🚨 [CookieService] Invalid domain provided:', domain);
        return;
      }
      cookieString += `; domain=${domain}`;
    } else if (this.isProductionDomain()) {
      // SECURITY FIX: Set domain spesifik berdasarkan current domain, bukan wildcard
      const currentDomain = this.getCurrentSpecificDomain();
      if (currentDomain) {
        cookieString += `; domain=${currentDomain}`;
      }
    }
    
    // SECURITY FIX: Selalu gunakan secure flag jika protokol HTTPS
    const isSecure = window.location.protocol === 'https:';
    if (isSecure) {
      cookieString += `; secure`;
    }
    
    // CROSS-DOMAIN FIX: Gunakan SameSite=None untuk cross-domain cookies dengan Secure
    if (name.includes('auth') || name.includes('token')) {
      if (isSecure) {
        cookieString += `; samesite=none`; // None untuk cross-domain (membutuhkan Secure)
      } else {
        cookieString += `; samesite=lax`; // Lax untuk HTTP/development
      }
    } else {
      cookieString += `; samesite=lax`;
    }
    
    // SECURITY FIX: Tambahkan HttpOnly untuk auth cookies (jika memungkinkan)
    if (name.includes('auth') || name.includes('token')) {
      // Note: HttpOnly tidak bisa diset via JavaScript, ini untuk dokumentasi
      console.warn('⚠️ [CookieService] Consider setting HttpOnly flag server-side for:', name);
    }
    
    document.cookie = cookieString;
  }

  /**
   * Get cookie value - SECURED VERSION
   */
  getCookie(name: string): string | null {
    // SECURITY FIX: Validasi akses cookie
    if (!this.canAccessCookie(name)) {
      return null;
    }

    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const value = decodeURIComponent(c.substring(nameEQ.length, c.length));
        
        // SECURITY FIX: Validasi nilai cookie untuk auth tokens
        if ((name.includes('auth') || name.includes('token')) && !this.isValidTokenFormat(value)) {
          console.warn('🚨 [CookieService] Invalid token format detected:', name);
          return null;
        }
        
        return value;
      }
    }
    
    return null;
  }

  /**
   * Delete cookie
   */
  deleteCookie(name: string, domain?: string): void {
    const baseCookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/`;

    const deleteWithDomain = (d?: string) => {
      if (!d) {
        document.cookie = baseCookieString;
        return;
      }

      if (!this.isValidDomain(d)) {
        console.error('🚨 [CookieService] Invalid domain provided for deletion:', d);
        return;
      }

      document.cookie = `${baseCookieString}; domain=${d}`;
    };

    deleteWithDomain(undefined);

    if (domain) {
      deleteWithDomain(domain);
      if (domain.startsWith('.')) {
        deleteWithDomain(domain.substring(1));
      } else {
        deleteWithDomain('.' + domain);
      }
      return;
    }

    if (this.isProductionDomain()) {
      const currentDomain = this.getCurrentSpecificDomain();
      if (currentDomain) {
        deleteWithDomain(currentDomain);
        deleteWithDomain(currentDomain.startsWith('.') ? currentDomain.substring(1) : '.' + currentDomain);
      }
    }
  }

  /**
   * Check if we're on production domain
   */
  private isProductionDomain(): boolean {
    const hostname = window.location.hostname;
    return hostname !== 'localhost' && hostname !== '127.0.0.1';
  }

  /**
   * SECURITY FIX: Validasi domain yang diizinkan
   */
  private isValidDomain(domain: string): boolean {
    if (domain === 'localhost') return true;
    
    const currentBase = this.getCurrentSpecificDomain();
    if (!currentBase) return false;
    
    // Allow if domain matches current base or is a subdomain of it
    return domain.endsWith(currentBase) || domain === currentBase.substring(1);
  }

  /**
   * CROSS-DOMAIN FIX: Mendapatkan domain untuk cross-subdomain cookie sharing
   * Supports: admaxhub.com, mataloka.com, etc.
   */
  public getCurrentSpecificDomain(): string | null {
    const hostname = window.location.hostname;
    
    // Untuk localhost, return null (tidak perlu domain)
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return null;
    }
    
    // Pattern for known subdomains
    // e.g. login.mataloka.com -> .mataloka.com
    // e.g. dsp.admaxhub.com -> .admaxhub.com
    const subdomainRegex = /^(login|join|dsp|ssp)\./;
    
    if (hostname.match(subdomainRegex)) {
      return '.' + hostname.replace(subdomainRegex, '');
    }
    
    // Fallback: if user accesses via bare domain or unknown subdomain, 
    // try to be smart about it.
    // Ideally, we want to return the base domain with a dot.
    
    // Legacy support: admaxhub.com explicit check (just in case)
    if (hostname.includes('admaxhub.com')) {
       return '.admaxhub.com';
    }
    
    // Generic fallback for other domains (e.g. mataloka.com)
    // If it has at least one dot, assume it's a domain we can wildcard.
    // e.g. mataloka.com -> .mataloka.com
    if (hostname.includes('.')) {
      const parts = hostname.split('.').filter(Boolean);
      if (parts.length >= 2) {
        const base = parts.slice(-2).join('.');
        return '.' + base;
      }
      return '.' + hostname;
    }

    return null;
  }

  /**
    * SECURITY FIX: Validasi akses cookie berdasarkan domain
    */
   private canAccessCookie(cookieName: string): boolean {
     const currentHostname = window.location.hostname;
     
     // Untuk localhost, izinkan semua
     if (currentHostname === 'localhost' || currentHostname === '127.0.0.1') {
       return true;
     }
     
     // Untuk auth cookies, hanya izinkan di domain yang sesuai
     if (cookieName.includes('auth') || cookieName.includes('token')) {
       const currentBase = this.getCurrentSpecificDomain();
       if (currentBase) {
         // Check if current hostname belongs to the base domain
         return currentHostname.endsWith(currentBase) || currentHostname === currentBase.substring(1);
       }
     }
     
     return true;
   }

   /**
    * SECURITY FIX: Validasi format token
    */
   private isValidTokenFormat(token: string): boolean {
     if (!token || token.length < 10) {
       return false;
     }
     
     // Basic validation untuk JWT format (header.payload.signature)
     if (token.includes('.')) {
       const parts = token.split('.');
       return parts.length === 3 && parts.every(part => part.length > 0);
     }
     
     // Untuk token format lain, minimal length check
     return token.length >= 20;
   }

  /**
   * Get all cookies as object
   */
  getAllCookies(): Record<string, string> {
    const cookies: Record<string, string> = {};
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      const eqPos = c.indexOf('=');
      if (eqPos > 0) {
        const name = c.substring(0, eqPos);
        const value = decodeURIComponent(c.substring(eqPos + 1));
        cookies[name] = value;
      }
    }
    
    return cookies;
  }

  /**
   * Check if cookie exists
   */
  hasCookie(name: string): boolean {
    return this.getCookie(name) !== null;
  }
}
